import {
  BOARD_IMAGE_MAX_BYTES,
  BOARD_IMAGE_MAX_EDGE,
  BOARD_IMAGE_SOURCE_MAX_BYTES,
  isBoardImageType,
  type BoardImageType,
} from "@edu/shared";

/**
 * Ужатие картинки, брошенной на доску, ДО отправки на сервер. Проект целиком:
 * `docs/room-feedback-fixes.md`, раздел 2.
 *
 * Почему в браузере: ffmpeg и ImageMagick у нас нет и не будет (раздел 8
 * CLAUDE.md), а декодер картинок есть ровно в одном месте нашей системы и ровно
 * в один момент, пока файл в руках у человека. Прецедент тот же, что у кадра для
 * плитки медиатеки (`features/media/preview.ts`).
 *
 * ⚠️ Отказ по размеру это неправильный рычаг, правильный это ужатие (раздел 2.3
 * проекта). Фотография тетради с телефона это 3-5 МБ, и её надо принять и
 * ужать, а не отвергнуть в худший момент из возможных.
 *
 * План ужатия вынесен ЧИСТОЙ функцией и покрыт тестами не ради ритуала: числа
 * здесь общие с сервером (`@edu/shared/board-image.ts`), и разъехавшийся план
 * означает картинку, принятую браузером и отвергнутую сервером уже ПОСЛЕ
 * отправки по мобильному каналу.
 */

export interface BoardImageAttempt {
  type: BoardImageType;
  /** Для `image/png` значение не используется, `toBlob` его игнорирует. */
  quality: number;
}

export interface BoardImagePlan {
  width: number;
  height: number;
  /**
   * Лестница попыток кодирования, сверху вниз.
   *
   * ⚠️ Пустой список это НЕ «нечем кодировать», а «перекодировать нечего»:
   * картинка и так в пределах длинной стороны, весит меньше потолка и уже
   * приемлемого типа. Тогда уезжают исходные байты, и это лучший из исходов:
   * ни потери качества, ни лишней работы на телефоне.
   */
  attempts: readonly BoardImageAttempt[];
}

/**
 * Лестница для источника, у которого может быть прозрачность.
 *
 * PNG первым, потому что рукописный текст, скриншот и вырезанный фрагмент с
 * прозрачным фоном это самые частые картинки на уроке, и потеря альфы у них
 * видна сразу (чёрный прямоугольник вместо фона). JPEG стоит последним ровно
 * как аварийный: лучше картинка с залитым фоном, чем отказ.
 */
const ALPHA_LADDER: readonly BoardImageAttempt[] = [
  { type: "image/png", quality: 1 },
  { type: "image/webp", quality: 0.9 },
  { type: "image/webp", quality: 0.75 },
  { type: "image/jpeg", quality: 0.8 },
];

/** У JPEG альфы нет по построению, поэтому сразу лучшее сжатие. */
const OPAQUE_LADDER: readonly BoardImageAttempt[] = [
  { type: "image/webp", quality: 0.85 },
  { type: "image/jpeg", quality: 0.82 },
  { type: "image/jpeg", quality: 0.6 },
];

/**
 * Потолки ужатия. `lossy` требует на выходе ЯВНО JPEG или WebP (материал
 * домашки, 1.2 `docs/homework-scratch-design.md`): это фото раздатки и скриншоты
 * банка, прозрачность им не нужна, а PNG первой ступенью дал бы скриншоту
 * страницы вес в разы больше, чем стоит его чтение на телефоне ученика.
 */
export interface BoardImageLimits {
  maxEdge?: number;
  maxBytes?: number;
  lossy?: boolean;
}

function lossyType(contentType: string): boolean {
  return contentType === "image/jpeg" || contentType === "image/webp";
}

export function boardImagePlan(
  source: { contentType: string; sizeBytes: number; width: number; height: number },
  limits?: BoardImageLimits,
): BoardImagePlan {
  const maxEdge = limits?.maxEdge ?? BOARD_IMAGE_MAX_EDGE;
  const maxBytes = limits?.maxBytes ?? BOARD_IMAGE_MAX_BYTES;

  const longest = Math.max(source.width, source.height);
  const scale = longest > maxEdge ? maxEdge / longest : 1;
  // Единица снизу, иначе полоска 3000x1 превращается в канвас нулевой высоты,
  // а `drawImage` в такой не рисует вовсе
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));

  const lossy = limits?.lossy === true;
  const untouched =
    scale === 1 &&
    source.sizeBytes <= maxBytes &&
    isBoardImageType(source.contentType) &&
    (!lossy || lossyType(source.contentType));

  return {
    width,
    height,
    attempts: untouched
      ? []
      : lossy || source.contentType === "image/jpeg"
        ? OPAQUE_LADDER
        : ALPHA_LADDER,
  };
}

/**
 * Отказ ужатия, ещё до сервера. Код совпадает с серверным словарём там, где
 * серверу есть что сказать о том же: так текст для человека остаётся один и
 * тот же, откуда бы отказ ни пришёл.
 */
export class BoardImageError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(boardImageErrorText(code));
    this.code = code;
  }
}

/**
 * Текст отказа для человека. По-русски и без терминов: это читает школьник.
 *
 * ⚠️ `board_image_limit` и `quota_exceeded` НЕ склеиваются (раздел 2.4 проекта):
 * первое лечится разрешением преподавателя, второе не лечится ничем, кроме
 * уборки в библиотеке. Один текст на два случая врал бы в одном из них.
 *
 * `plan_not_found` это заготовка, которую успели убрать в корзину. Он здесь, а
 * не у доски заготовки, с 07.10.2026: ту же ручку зовёт окно «Текст с
 * картинки» (выбор картинки прямо в окне), и второй словарь отказов рядом с
 * этим разошёлся бы с ним молча.
 */
export function boardImageErrorText(code: string): string {
  switch (code) {
    case "board_image_limit":
      return "Больше картинок за это занятие добавить нельзя. Попросите преподавателя.";
    case "quota_exceeded":
      return "У преподавателя закончилось место для файлов, картинка не сохранилась.";
    case "file_too_large":
    case "source_too_large":
      return "Картинка слишком большая. Попробуйте другую или снимок поменьше.";
    case "content_type_mismatch":
    case "image_broken":
      return "Эту картинку открыть не получилось. Подойдут PNG, JPEG и WebP.";
    case "lesson_ended":
      return "Занятие уже завершено, добавить картинку нельзя.";
    case "lesson_not_found":
      return "Нет доступа к этому занятию, картинка не сохранилась.";
    case "plan_not_found":
      return "Заготовка не найдена: возможно, её убрали в корзину. Картинка не сохранилась.";
    default:
      return "Картинка не сохранилась. Проверьте связь и попробуйте ещё раз.";
  }
}

export interface EncodedBoardImage {
  blob: Blob;
  width: number;
  height: number;
}

/**
 * Ужатая картинка, готовая к отправке. Бросает `BoardImageError`.
 *
 * Декодируем через `<img>`, а не `createImageBitmap`: Excalidraw принимает и
 * SVG, а его `createImageBitmap` умеет не везде.
 *
 * ⚠️ **Потолки приезжают параметром, а второй реализации ужатия нет и быть не
 * должно** (раздел 7.1 `docs/homework-photo-design.md`). Фотография решения к
 * заданию домашки ужимается сильнее доски (1600 px и 800 КБ против 2000 px и
 * 2 МБ), и это ровно два числа разницы: почерк читается и в километре от
 * исходного размера, а лишний мегабайт оплачивается минутой ожидания
 * школьника. Скопированная сюда вторая лестница кодеков разошлась бы с этой на
 * первой же правке, и разошлась бы молча.
 */
export async function encodeBoardImage(
  source: Blob,
  limits?: BoardImageLimits,
): Promise<EncodedBoardImage> {
  const maxBytes = limits?.maxBytes ?? BOARD_IMAGE_MAX_BYTES;
  if (source.size > BOARD_IMAGE_SOURCE_MAX_BYTES) throw new BoardImageError("source_too_large");

  const url = URL.createObjectURL(source);
  let image: HTMLImageElement;
  try {
    image = await decode(url);
  } finally {
    URL.revokeObjectURL(url);
  }

  const plan = boardImagePlan(
    {
      contentType: source.type,
      sizeBytes: source.size,
      width: image.naturalWidth,
      height: image.naturalHeight,
    },
    limits,
  );
  if (image.naturalWidth === 0 || image.naturalHeight === 0) throw new BoardImageError("image_broken");
  if (plan.attempts.length === 0) return { blob: source, width: plan.width, height: plan.height };

  const canvas = document.createElement("canvas");
  canvas.width = plan.width;
  canvas.height = plan.height;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new BoardImageError("image_broken");
  // Прозрачное в JPEG становится чёрным: скриншот с прозрачным фоном читался бы
  // белыми буквами по чёрному. Подложка белая, как бумага, с которой его сняли
  if (limits?.lossy === true) {
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, plan.width, plan.height);
  }
  ctx.drawImage(image, 0, 0, plan.width, plan.height);

  for (const attempt of plan.attempts) {
    const blob = await toBlob(canvas, attempt);
    // Тип проверяем у самого блоба: `toBlob` с неподдержанным типом молча
    // отдаёт PNG, а PNG вместо webp это не «чуть больше», а в разы больше
    if (blob === null || blob.type !== attempt.type) continue;
    if (blob.size <= maxBytes) return { blob, width: plan.width, height: plan.height };
  }
  throw new BoardImageError("file_too_large");
}

function decode(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new BoardImageError("image_broken"));
    el.src = url;
  });
}

function toBlob(canvas: HTMLCanvasElement, attempt: BoardImageAttempt): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), attempt.type, attempt.quality);
  });
}
